/**
 * Reads the server's base check report (`/base-check`) into the routing the
 * catalogue uses. Pure: the request side lives in `api/routingProvider.ts`,
 * and the MCP server (mcp-server/) uses this without React or axios.
 */
import { Coverage, RoutingInfo, RoutingQuestion } from "../types/catalogue"

export type RoutingResult =
  | { ok: true; info: RoutingInfo }
  | {
      ok: false
      reason: "unavailable" | "no-questionnaire" | "no-data" | "failed"
      message: string
    }

export const UNAVAILABLE: RoutingResult = {
  ok: false,
  reason: "unavailable",
  message: "Base from routing is not available on this server.",
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v)
const num = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? v : 0)
const COVERAGE: Coverage[] = ["full", "partial", "unsupported", "failed"]
const coverage = (v: unknown): Coverage =>
  COVERAGE.includes(v as Coverage) ? (v as Coverage) : "unsupported"
const text = (v: unknown): string | null =>
  typeof v === "string" && v.trim() ? v : null

/** Reads a base check report. Anything that is not a report is "unavailable". */
export const readRouting = (body: unknown): RoutingResult => {
  let value = body
  if (typeof value === "string") {
    try {
      value = JSON.parse(value)
    } catch {
      return UNAVAILABLE
    }
  }
  // A report always has `status` and `questions`, whatever its status; an
  // error body (`{"Err msg": ...}`) has neither.
  if (!isRecord(value) || typeof value.status !== "string" || !Array.isArray(value.questions))
    return UNAVAILABLE
  if (value.status === "no_qnr")
    return {
      ok: false,
      reason: "no-questionnaire",
      message: "The project has no questionnaire, so bases cannot follow the routing.",
    }
  if (value.status === "no_data")
    return {
      ok: false,
      reason: "no-data",
      message: "There is no data yet, so bases from routing were not checked.",
    }
  if (value.status !== "ok")
    return {
      ok: false,
      reason: "failed",
      message: `The routing could not be read (${value.status}${
        text(value.error) ? `: ${text(value.error)}` : ""
      }).`,
    }

  const questions: Record<string, RoutingQuestion> = {}
  value.questions.forEach((q) => {
    if (!isRecord(q) || typeof q.label !== "string" || !Array.isArray(q.instances)) return
    const instances = q.instances.filter(isRecord)
    const first = instances[0]
    questions[q.label] = {
      asked: text(q.askedExpression),
      coverage: coverage(q.coverage),
      offPath: num(q.offPath),
      missingOnPath: num(q.missingOnPath),
      // The server gives one condition per question, that of the first iteration.
      looped:
        instances.length !== 1 ||
        (Array.isArray(first?.iteration) && first.iteration.length > 0),
    }
  })
  return {
    ok: true,
    info: {
      variant: typeof value.variant === "string" ? value.variant : "",
      dataHash: typeof value.dataHash === "string" ? value.dataHash : "",
      questions,
    },
  }
}
